import { ButtonWithDialog, StatusProxy } from "@focus/focusUI";
import React, { useEffect, useMemo, useState } from "react";
import { ApiKeyCreatedDialog } from "../components/Settings/ApiKeys/ApiKeyCreatedDialog";
import { ApiKeyForm } from "../components/Settings/ApiKeys/ApiKeysForm";
import { TableShell } from "../components/table/TableShell";
import { useApiKeys } from "../hooks/useApiKeys";
import type { ApiKey, ApiKeyConfig } from "../types/apiKeys";
import { ApiKeysTable } from "../components/Settings/ApiKeys/ApiKeysTable";

const tabs = [
  {
    id: "active",
    title: "Active",
    description: "Available API keys",
    filter: (apiKeys: ApiKey[]) =>
      apiKeys.filter((apiKey) => !apiKey.expired && !apiKey.revoked),
    render: (apiKeys: ApiKey[], removeApiKey: (id: string) => void) => (
      <ApiKeysTable
        apiKeys={apiKeys.filter((apiKey) => !apiKey.expired && !apiKey.revoked)}
        canDelete
        key="active-api-keys-table"
        onDelete={removeApiKey}
      />
    ),
  },
  {
    id: "expired",
    title: "Expired / Revoked",
    description: "Last week history",
    filter: (apiKeys: ApiKey[]) =>
      apiKeys.filter((apiKey) => apiKey.expired || apiKey.revoked),
    render: (apiKeys: ApiKey[]) => (
      <ApiKeysTable
        apiKeys={apiKeys.filter((apiKey) => apiKey.expired || apiKey.revoked)}
        key="expired-api-keys-table"
      />
    ),
  },
];

export const ApiKeysContainer: React.FC = () => {
  const {
    apiKeys,
    loadApiKeys,
    generateApiKey,
    removeApiKey,
    lastCreatedApiKey,
    removeLastCreatedApiKey,
    getRequestStatus,
    getRequestError,
  } = useApiKeys();

  useEffect(() => {
    loadApiKeys();
  }, []);

  const [selectedTabId, setSelectedTabId] = useState(tabs[0].id);
  const safeApiKeys = apiKeys ?? [];
  const selectedTab = tabs.find((tab) => tab.id === selectedTabId) ?? tabs[0];

  const counts = useMemo(
    () => ({
      active: tabs[0].filter(safeApiKeys).length,
      expired: tabs[1].filter(safeApiKeys).length,
      total: safeApiKeys.length,
    }),
    [safeApiKeys],
  );

  const status = getRequestStatus("getApiKeys");
  const error = getRequestError("getApiKeys");

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-4">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase text-slate-500">
            Credentials
          </div>
          <h2 className="mt-1 text-lg font-bold text-slate-950">API Keys</h2>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-medium text-slate-500">
            <span>{counts.total} total</span>
            <span className="text-slate-300">/</span>
            <span>{counts.active} active</span>
            <span className="text-slate-300">/</span>
            <span>{counts.expired} expired or revoked</span>
          </div>
        </div>

        {selectedTabId !== "expired" && (
          <ButtonWithDialog
            className="focusoc-primary-button"
            defaultContent={{
              name: "",
              roles: [],
              allowedIps: [],
              expirationDays: 30,
            }}
            form={({ onUpdate, content }) => (
              <ApiKeyForm value={content as ApiKeyConfig} onChange={onUpdate} />
            )}
            icon="plus"
            intent="none"
            onAccept={async (content) => {
              generateApiKey(content as ApiKeyConfig);
            }}
            text="Generate API key"
          />
        )}
      </header>

      <div className="border-b border-slate-200 bg-slate-50/70 px-4 py-3">
        <div
          aria-label="API key status"
          className="inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1 shadow-sm"
          role="tablist"
        >
          {tabs.map((tab) => {
            const isSelected = tab.id === selectedTabId;
            const count = tab.filter(safeApiKeys).length;

            return (
              <button
                aria-selected={isSelected}
                className={`cursor-pointer rounded-lg px-3 py-1.5 text-left text-sm font-semibold transition ${
                  isSelected
                    ? "focusoc-theme-surface shadow-sm"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                }`}
                key={tab.id}
                onClick={() => setSelectedTabId(tab.id)}
                role="tab"
                type="button"
              >
                <span>{tab.title}</span>
                <span
                  className={`ml-2 rounded-full px-2 py-0.5 text-xs ${
                    isSelected
                      ? "bg-white/15 text-white"
                      : "bg-slate-100 text-slate-500"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <ApiKeyCreatedDialog
        apiKey={lastCreatedApiKey ?? null}
        isOpen={Boolean(lastCreatedApiKey)}
        onClose={() => removeLastCreatedApiKey()}
      />

      <StatusProxy error={error} status={status}>
        <div className="min-h-0 flex-1 overflow-hidden p-4">
          <TableShell>
            {selectedTab.render(safeApiKeys, removeApiKey)}
          </TableShell>
        </div>
      </StatusProxy>
    </div>
  );
};
